import React from 'react';

import { formatMoney } from '@betterment/core';
import { Box, Table, Text } from '@betterment/samba-react';

import { FeeAttemptFailureReason } from '../../../../graphql/schema';
import { LEGAL_ACCOUNT_TYPE_DISPLAY_NAMES } from '../../../../Shared/constants';
import { ERROR_MESSAGES, FeeAttemptFailure, FeeAttemptInputAccount, FeeAttemptInputAmount } from '../data';

type FormattedAccount = { kind: 'invalid'; name: string } | { kind: 'valid'; name: string; legalAccountType: string };

interface GroupedFailureRowProps {
  accountKey: string;
  failures: FeeAttemptFailure[];
}

const formatAccount = (account: FeeAttemptInputAccount): FormattedAccount => {
  if (account.__typename === 'InvalidAccountString') {
    return { kind: 'invalid', name: account.input };
  }

  return {
    kind: 'valid',
    name: account.name,
    legalAccountType: LEGAL_ACCOUNT_TYPE_DISPLAY_NAMES[account.legalAccount.type],
  };
};

const formatAmount = (amount: FeeAttemptInputAmount): string => {
  if (amount.__typename === 'InvalidAmountString') {
    return amount.input;
  }
  return formatMoney(amount.amount);
};

const getErrorHelperText = (reason: FeeAttemptFailureReason, accountName: string): string | undefined => {
  const helperTextMap: Partial<Record<FeeAttemptFailureReason, string>> = {
    [FeeAttemptFailureReason.ExceedsMaxFeeAmount]: '$1,000,000',
    [FeeAttemptFailureReason.InsufficientBalance]: `Exceeds 90% of ${accountName} account balance`,
    [FeeAttemptFailureReason.InsufficientBalanceCombined]: `Combined fees exceed 90% of ${accountName} account balance`,
  };
  return helperTextMap[reason];
};

export const GroupedFailureRow: React.FC<GroupedFailureRowProps> = ({ accountKey, failures }) => {
  const firstFailure = failures[0];
  const formattedAccount = formatAccount(firstFailure.account);

  // Comma-separated row numbers
  const rowNumbers = failures.map(f => f.row).join(', ');

  return (
    <Table.Row>
      <Table.Cell css={{ width: '10%', wordBreak: 'break-word' }}>
        <Text>{rowNumbers}</Text>
      </Table.Cell>
      <Table.Cell css={{ width: '25%', wordBreak: 'break-word' }}>
        <Box css={{ display: 'flex', flexDirection: 'column' }}>
          <Text>{formattedAccount.kind === 'valid' ? formattedAccount.name : accountKey}</Text>
          {formattedAccount.kind === 'valid' && (
            <Text color="text.weak" variant="text50">
              {formattedAccount.legalAccountType}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell css={{ width: '20%', wordBreak: 'break-word' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.150' }}>
          {failures.map(failure => (
            <Box key={failure.row} css={{ display: 'flex', flexDirection: 'column' }}>
              <Text>{formatAmount(failure.amount)}</Text>
              <Text color="text.weak" variant="text50">
                Row {failure.row}
              </Text>
            </Box>
          ))}
        </Box>
      </Table.Cell>
      <Table.Cell css={{ width: '45%', wordBreak: 'break-word' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.100' }}>
          {firstFailure.failureReasons.map(error => {
            const helperText = getErrorHelperText(error, formatAccount(firstFailure.account).name);
            return (
              <Box key={error} css={{ display: 'flex', flexDirection: 'column' }}>
                <Text>{ERROR_MESSAGES[error]}</Text>
                {helperText && (
                  <Text color="text.weak" variant="text50" data-testid="error-helper-text">
                    {helperText}
                  </Text>
                )}
              </Box>
            );
          })}
        </Box>
      </Table.Cell>
    </Table.Row>
  );
};
